import React, { useMemo, useState } from 'react';
import { AlertTriangle, BarChart3, CalendarDays, RefreshCw, Scale, TrendingUp } from 'lucide-react';
import { api } from '../services/api.js';
import './FinancialStatements.css';

const REPORTS = {
    trial: { label: 'ميزان المراجعة', description: 'حركة القيود المرحلة حتى تاريخ القطع', icon: Scale },
    income: { label: 'قائمة الدخل', description: 'الإيرادات والمصروفات ضمن فترة محددة', icon: TrendingUp },
    balance: { label: 'الميزانية العمومية', description: 'المركز المالي في تاريخ قطع محدد', icon: BarChart3 },
};

const money = (value) => {
    if (typeof value !== 'string' && typeof value !== 'number') return '—';
    const amount = Number(value);
    return Number.isFinite(amount)
        ? new Intl.NumberFormat('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
        : '—';
};

const numberValue = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
};

const equalMoney = (first, second) => {
    const left = numberValue(first);
    const right = numberValue(second);
    return left !== null && right !== null && Math.abs(left - right) < 0.005;
};

const RowTable = ({ rows = [], columns, emptyMessage }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return <p className="financial-statements-table-empty">{emptyMessage}</p>;
    }

    return (
        <div className="financial-statements-table-wrap">
            <table className="financial-statements-table">
                <thead>
                    <tr>{columns.map((column) => <th key={column.key} scope="col">{column.label}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.account_id || row.code}>
                            {columns.map((column) => (
                                <td key={column.key} data-label={column.label}>
                                    {column.render ? column.render(row) : row[column.key] || '—'}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Field = ({ label, value, onChange, testId }) => (
    <label className="financial-statements-field">
        <span>{label}</span>
        <input
            type="date"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            data-testid={testId}
        />
    </label>
);

const MetricCard = ({ label, value, note, tone = 'blue', testId }) => (
    <article className={`financial-statements-metric financial-statements-metric--${tone}`} data-testid={testId}>
        <p>{label}</p>
        <strong dir="ltr">{money(value)}</strong>
        {note && <span>{note}</span>}
    </article>
);

const StatementSection = ({ title, total, children }) => (
    <section className="financial-statements-section">
        <header>
            <h3>{title}</h3>
            <strong dir="ltr">{money(total)}</strong>
        </header>
        {children}
    </section>
);

export default function FinancialStatements() {
    const [kind, setKind] = useState('trial');
    const [trialAsOf, setTrialAsOf] = useState('');
    const [incomeFrom, setIncomeFrom] = useState('');
    const [incomeTo, setIncomeTo] = useState('');
    const [profitFrom, setProfitFrom] = useState('');
    const [balanceAsOf, setBalanceAsOf] = useState('');
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const fields = useMemo(() => {
        if (kind === 'trial') {
            return <Field label="تاريخ القطع" value={trialAsOf} onChange={setTrialAsOf} testId="financial-statements-trial-as-of" />;
        }
        if (kind === 'income') {
            return <div className="financial-statements-period-grid">
                <Field label="من" value={incomeFrom} onChange={setIncomeFrom} testId="financial-statements-income-from" />
                <Field label="إلى" value={incomeTo} onChange={setIncomeTo} testId="financial-statements-income-to" />
            </div>;
        }
        return <div className="financial-statements-period-grid">
            <Field label="بداية فترة الربح المعتمدة" value={profitFrom} onChange={setProfitFrom} testId="financial-statements-profit-from" />
            <Field label="تاريخ الميزانية" value={balanceAsOf} onChange={setBalanceAsOf} testId="financial-statements-balance-as-of" />
        </div>;
    }, [balanceAsOf, incomeFrom, incomeTo, kind, profitFrom, trialAsOf]);

    const request = async () => {
        let path = '';
        let params = {};
        if (kind === 'trial') {
            if (!trialAsOf) {
                setError('اختر تاريخ القطع قبل عرض ميزان المراجعة.');
                return;
            }
            path = '/financial-statements/trial-balance';
            params = { as_of_date: trialAsOf };
        } else if (kind === 'income') {
            if (!incomeFrom || !incomeTo) {
                setError('اختر تاريخي البداية والنهاية قبل عرض قائمة الدخل.');
                return;
            }
            if (incomeFrom > incomeTo) {
                setError('تاريخ البداية يجب ألا يتجاوز تاريخ النهاية.');
                return;
            }
            path = '/financial-statements/income-statement';
            params = { start_date: incomeFrom, end_date: incomeTo };
        } else {
            if (!profitFrom || !balanceAsOf) {
                setError('اختر بداية فترة الربح وتاريخ الميزانية قبل العرض.');
                return;
            }
            if (profitFrom > balanceAsOf) {
                setError('بداية فترة الربح يجب ألا تتجاوز تاريخ الميزانية.');
                return;
            }
            path = '/financial-statements/balance-sheet';
            params = { profit_from_date: profitFrom, as_of_date: balanceAsOf };
        }

        setError('');
        setReport(null);
        setLoading(true);
        try {
            const response = await api.get(path, { params });
            if (response?.data?.success !== true || !response?.data?.report) throw new Error('REPORT_CONTRACT_INVALID');
            setReport(response.data.report);
        } catch (_error) {
            // A source failure stays explicit. It must never become a zero-filled report.
            setError('بيانات التقرير غير متاحة؛ تعذر التحقق من القيود المرحلة والمتوازنة.');
        } finally {
            setLoading(false);
        }
    };

    const selectReport = (nextKind) => {
        setKind(nextKind);
        setReport(null);
        setError('');
    };

    const statementRows = [
        { key: 'code', label: 'الرمز' },
        { key: 'name_ar', label: 'الحساب' },
        { key: 'amount', label: 'المبلغ', render: (row) => money(row.amount) },
    ];
    const trialRows = [
        { key: 'code', label: 'الرمز' },
        { key: 'name_ar', label: 'الحساب' },
        { key: 'debit', label: 'مدين', render: (row) => money(row.debit) },
        { key: 'credit', label: 'دائن', render: (row) => money(row.credit) },
        { key: 'balance', label: 'الرصيد', render: (row) => money(row.balance) },
    ];

    const selected = REPORTS[kind];
    const trialBalanced = kind === 'trial' && report && equalMoney(report.totals?.debit, report.totals?.credit);
    const balanceBalanced = kind === 'balance' && report && equalMoney(report.totals?.assets, report.totals?.liabilities_and_equity);
    const incomeNet = numberValue(report?.net_profit);

    return (
        <main dir="rtl" className="financial-statements" data-testid="financial-statements-page">
            <section className="financial-statements-shell">
                <header className="financial-statements-hero">
                    <div className="financial-statements-hero-icon"><Scale size={26} /></div>
                    <div>
                        <p className="financial-statements-eyebrow">التقارير المعتمدة</p>
                        <h1>القوائم المالية</h1>
                        <p>قراءة فقط من القيود المرحلة والمتوازنة والحسابات القابلة للترحيل مباشرةً.</p>
                    </div>
                    <span className="financial-statements-readonly">قراءة فقط</span>
                </header>

                <section className="financial-statements-control-card" aria-label="اختيار التقرير والفترة">
                    <div className="financial-statements-tabs" role="tablist" aria-label="القوائم المالية">
                        {Object.entries(REPORTS).map(([key, item]) => {
                            const Icon = item.icon;
                            const active = kind === key;
                            return <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => selectReport(key)}
                                data-testid={`financial-statements-tab-${key}`}
                                className={active ? 'is-active' : ''}
                            >
                                <Icon size={18} />
                                <span>{item.label}</span>
                            </button>;
                        })}
                    </div>
                    <div className="financial-statements-control-copy">
                        <strong>{selected.label}</strong>
                        <span>{selected.description}</span>
                    </div>
                    <div className="financial-statements-filter-row">
                        <div>{fields}</div>
                        <button type="button" onClick={request} disabled={loading} data-testid="financial-statements-load" className="financial-statements-load">
                            <RefreshCw size={17} className={loading ? 'is-spinning' : ''} />
                            {loading ? 'جارٍ التحقق…' : 'عرض التقرير'}
                        </button>
                    </div>
                </section>

                {error && <section className="financial-statements-state financial-statements-state--unavailable" role="alert" data-testid="financial-statements-unavailable">
                    <AlertTriangle size={22} aria-hidden="true" />
                    <div><h2>بيانات التقرير غير متاحة</h2><p>{error}</p></div>
                </section>}

                {!report && !error && <section className="financial-statements-state" data-testid="financial-statements-empty">
                    <CalendarDays size={28} aria-hidden="true" />
                    <div><h2>اختر فترة التقرير</h2><p>لن تُفترض سنة مالية أو ضريبة أو إقفال. ستظهر نتيجة القارئ المعتمد بعد اختيار الفترة صراحةً.</p></div>
                </section>}

                {report && kind === 'trial' && <section className="financial-statements-report" data-testid="financial-statements-trial-report">
                    <div className="financial-statements-report-heading">
                        <div><p>ميزان المراجعة</p><h2>حتى {report.as_of_date}</h2></div>
                        <span className={trialBalanced ? 'is-balanced' : 'is-unverified'}>{trialBalanced ? 'متوازن' : 'تعذر تأكيد التوازن'}</span>
                    </div>
                    <div className="financial-statements-metrics-grid financial-statements-metrics-grid--two">
                        <MetricCard label="إجمالي المدين" value={report.totals?.debit} note="من القيود المرحلة" tone="sky" testId="financial-statements-trial-debit" />
                        <MetricCard label="إجمالي الدائن" value={report.totals?.credit} note="من القيود المرحلة" tone="violet" testId="financial-statements-trial-credit" />
                    </div>
                    <StatementSection title="تفاصيل ميزان المراجعة" total={report.totals?.debit}>
                        <RowTable rows={report.rows} columns={trialRows} emptyMessage="لا توجد حركات مرحلة حتى تاريخ القطع المحدد." />
                    </StatementSection>
                </section>}

                {report && kind === 'income' && <section className="financial-statements-report" data-testid="financial-statements-income-report">
                    <div className="financial-statements-report-heading">
                        <div><p>قائمة الدخل</p><h2>{report.period?.from} — {report.period?.to}</h2></div>
                        <span className="financial-statements-period-label">فترة محددة</span>
                    </div>
                    <div className="financial-statements-metrics-grid">
                        <MetricCard label="إجمالي الإيرادات" value={report.revenue?.total} note="ضمن الفترة المختارة" tone="sky" testId="financial-statements-income-revenue" />
                        <MetricCard label="إجمالي المصروفات" value={report.expenses?.total} note="ضمن الفترة المختارة" tone="amber" testId="financial-statements-income-expenses" />
                        <MetricCard label="صافي الربح" value={report.net_profit} note={incomeNet !== null && incomeNet < 0 ? 'خسارة للفترة' : 'نتيجة الفترة'} tone={incomeNet !== null && incomeNet < 0 ? 'rose' : 'emerald'} testId="financial-statements-income-net" />
                    </div>
                    <StatementSection title="الإيرادات" total={report.revenue?.total}>
                        <RowTable rows={report.revenue?.rows} columns={statementRows} emptyMessage="لا توجد إيرادات مرحلة في الفترة المحددة." />
                    </StatementSection>
                    <StatementSection title="المصروفات" total={report.expenses?.total}>
                        <RowTable rows={report.expenses?.rows} columns={statementRows} emptyMessage="لا توجد مصروفات مرحلة في الفترة المحددة." />
                    </StatementSection>
                </section>}

                {report && kind === 'balance' && <section className="financial-statements-report" data-testid="financial-statements-balance-report">
                    <div className="financial-statements-report-heading">
                        <div><p>الميزانية العمومية</p><h2>حتى {report.as_of_date}</h2></div>
                        <span className={balanceBalanced ? 'is-balanced' : 'is-unverified'}>{balanceBalanced ? 'متوازنة' : 'تعذر تأكيد التوازن'}</span>
                    </div>
                    <p className="financial-statements-report-note">بداية فترة الربح المعتمدة: {report.profit_period_from}</p>
                    <div className="financial-statements-metrics-grid financial-statements-metrics-grid--two">
                        <MetricCard label="إجمالي الأصول" value={report.totals?.assets} note="في تاريخ القطع" tone="sky" testId="financial-statements-balance-assets" />
                        <MetricCard label="الالتزامات وحقوق الملكية" value={report.totals?.liabilities_and_equity} note="في تاريخ القطع" tone="violet" testId="financial-statements-balance-liabilities-equity" />
                    </div>
                    <StatementSection title="الأصول" total={report.assets?.total}>
                        <RowTable rows={report.assets?.rows} columns={statementRows} emptyMessage="لا توجد أصول ذات حركة في المصدر المتحقق." />
                    </StatementSection>
                    <StatementSection title="الالتزامات" total={report.liabilities?.total}>
                        <RowTable rows={report.liabilities?.rows} columns={statementRows} emptyMessage="لا توجد التزامات ذات حركة في المصدر المتحقق." />
                    </StatementSection>
                    <StatementSection title="حقوق الملكية" total={report.equity?.total}>
                        <p className="financial-statements-current-profit">ربح الفترة الحالي <strong dir="ltr">{money(report.equity?.current_period_profit)}</strong></p>
                        <RowTable rows={report.equity?.rows} columns={statementRows} emptyMessage="لا توجد حقوق ملكية ذات حركة في المصدر المتحقق." />
                    </StatementSection>
                </section>}

            </section>
        </main>
    );
}
